import React from 'react';

import PropTypes from 'prop-types';
import { Header, Grid, Icon, Button, Divider } from 'semantic-ui-react';

import { valueShape } from './StarRatingSettings.types';
import GenerationSettings from '../GenerationSettings';
import NotificationSettings from '../NotificationSettings';
import { getStarName } from '../RatingMenu/RatingMenu.utils';
import {
  MANUAL_RESPONSE_TYPE,
  AUTOMATIC_RESPONSE_TYPE,
  APPROVAL_RESPONSE_TYPE,
  TEMPLATE_GENERATION_TYPE,
  AI_GENERATION_TYPE,
} from '../ResponseSettingsForm/ResponseSettingsForm.constants';

import './StarRatingSettings.scss';

const propTypes = {
  rating: PropTypes.string.isRequired,
  value: valueShape.isRequired,
  onChange: PropTypes.func.isRequired,
};

const defaultProps = {};

export default function StarRatingSettings({ rating, value, onChange }) {
  const { type, generator } = value.generationSettings;

  function onGenerationSettingsChange(newValues) {
    onChange('generationSettings', { ...value.generationSettings, ...newValues });
  }

  function onNotificationSettingsChange(newValues) {
    onChange('notificationSettings', { ...value.notificationSettings, ...newValues });
  }

  function onTypeChange(newType) {
    // If switching to Manual, generator should be null
    if (newType === MANUAL_RESPONSE_TYPE) {
      onGenerationSettingsChange({ type: newType, generator: null });
    } else {
      // If switching from Manual, default generator to Template if not set
      onGenerationSettingsChange({
        type: newType,
        generator: generator || TEMPLATE_GENERATION_TYPE,
      });
    }
  }

  function onGeneratorChange(newGenerator) {
    onGenerationSettingsChange({ generator: newGenerator });
  }

  const isManual = type === MANUAL_RESPONSE_TYPE;
  const isApproval = type === APPROVAL_RESPONSE_TYPE;
  const isAutomatic = type === AUTOMATIC_RESPONSE_TYPE;

  const isAi = generator === AI_GENERATION_TYPE;
  const isTemplate = generator === TEMPLATE_GENERATION_TYPE;

  return (
    <div className="star-rating-settings">
      <div className="selectors">
        <Grid columns={2} stackable>
          <Grid.Column width={8} className="type-column">
            <Header as="h3">How would you like to respond to {getStarName(rating)} reviews?</Header>
            <Button.Group basic fluid>
              <Button active={isManual} onClick={() => onTypeChange(MANUAL_RESPONSE_TYPE)}>
                <Icon name="edit" /> Manual
              </Button>
              <Button active={isApproval} onClick={() => onTypeChange(APPROVAL_RESPONSE_TYPE)}>
                <Icon name="check" /> Approval
              </Button>
              <Button active={isAutomatic} onClick={() => onTypeChange(AUTOMATIC_RESPONSE_TYPE)}>
                <Icon name="lightning" /> Automatic
              </Button>
            </Button.Group>
          </Grid.Column>
          <Grid.Column width={8} className="generator-column">
            <Header as="h3">How would you like to generate the response?</Header>
            <Button.Group basic fluid>
              <Button
                basic
                active={isTemplate}
                onClick={() => onGeneratorChange(TEMPLATE_GENERATION_TYPE)}
                disabled={isManual}
              >
                <Icon name="file alternate" /> Template
              </Button>
              <Button
                basic
                active={isAi}
                onClick={() => onGeneratorChange(AI_GENERATION_TYPE)}
                disabled={isManual}
              >
                <Icon name="cloud" /> Assistant Generated
              </Button>
            </Button.Group>
          </Grid.Column>
        </Grid>
      </div>
      {type !== MANUAL_RESPONSE_TYPE && (
        <GenerationSettings
          settings={value.generationSettings}
          onChange={(field, newValue) => onGenerationSettingsChange({ [field]: newValue })}
        />
      )}
      <Divider />
      <NotificationSettings
        settings={value.notificationSettings}
        onChange={(field, newValue) => onNotificationSettingsChange({ [field]: newValue })}
      />
    </div>
  );
}

StarRatingSettings.propTypes = propTypes;
StarRatingSettings.defaultProps = defaultProps;
